import { type UnknownAction } from "@reduxjs/toolkit";
import _ from "lodash";
import {
  ADD_REWARDS_TO_CATALOGUE_REQUEST,
  APPROVE_GAMIFICATION_REDEMPTION_REQUEST,
  APPROVING_GAMIFICATION_REDEMPTION_SUCCEEDED,
  APPROVING_GAMIFICATION_REDEMPTION_FAILED,
  CREATE_REWARD_REQUEST,
  CREATING_REWARD_SUCCEEDED,
  CREATING_REWARD_FAILED,
  DELETE_REWARD_REQUEST,
  DELETING_REWARD_SUCCEEDED,
  DELETING_REWARD_FAILED,
  FETCH_GAMIFICATION_BALANCE_REQUEST,
  FETCHING_GAMIFICATION_BALANCE_SUCCEEDED,
  FETCHING_GAMIFICATION_BALANCE_FAILED,
  FETCH_GAMIFICATION_LIFETIME_POINTS_REQUEST,
  FETCHING_GAMIFICATION_LIFETIME_POINTS_SUCCEEDED,
  FETCHING_GAMIFICATION_LIFETIME_POINTS_FAILED,
  FETCH_GAMIFICATION_POINT_TRIGGERS_REQUEST,
  FETCHING_GAMIFICATION_POINT_TRIGGERS_SUCCEEDED,
  FETCHING_GAMIFICATION_POINT_TRIGGERS_FAILED,
  FETCH_GAMIFICATION_REDEMPTIONS_REQUEST,
  FETCHING_GAMIFICATION_REDEMPTIONS_SUCCEEDED,
  FETCHING_GAMIFICATION_REDEMPTIONS_FAILED,
  FETCH_GAMIFICATION_REWARDS_REQUEST,
  FETCHING_GAMIFICATION_REWARDS_SUCCEEDED,
  FETCHING_GAMIFICATION_REWARDS_FAILED,
  FETCH_GAMIFICATION_TRANSACTIONS_REQUEST,
  FETCHING_GAMIFICATION_TRANSACTIONS_SUCCEEDED,
  FETCHING_GAMIFICATION_TRANSACTIONS_FAILED,
  FETCH_REWARDS_CATALOGUE_REQUEST,
  FETCHING_REWARDS_CATALOGUE_SUCCEEDED,
  FETCHING_REWARDS_CATALOGUE_FAILED,
  INVALIDATE_CREATE_REWARD_STATE,
  INVALIDATE_DELETE_REWARD_STATE,
  INVALIDATE_GAMIFICATION_REDEMPTION_REVIEW,
  INVALIDATE_GAMIFICATION_REWARD_REDEMPTION,
  MARK_GAMIFICATION_REDEMPTION_GRANTED_REQUEST,
  MARKING_GAMIFICATION_REDEMPTION_GRANTED_SUCCEEDED,
  MARKING_GAMIFICATION_REDEMPTION_GRANTED_FAILED,
  REDEEM_GAMIFICATION_REWARD_REQUEST,
  REDEEMING_GAMIFICATION_REWARD_SUCCEEDED,
  REDEEMING_GAMIFICATION_REWARD_FAILED,
  REJECT_GAMIFICATION_REDEMPTION_REQUEST,
  REJECTING_GAMIFICATION_REDEMPTION_SUCCEEDED,
  REJECTING_GAMIFICATION_REDEMPTION_FAILED,
  UPDATE_GAMIFICATION_POINT_TRIGGER_REQUEST,
  UPDATING_GAMIFICATION_POINT_TRIGGER_SUCCEEDED,
  UPDATING_GAMIFICATION_POINT_TRIGGER_FAILED,
  UPDATE_REWARD_REQUEST,
  UPDATING_REWARD_SUCCEEDED,
  UPDATING_REWARD_FAILED,
  FETCH_USER_GAMIFICATION_REDEMPTIONS_REQUEST,
  FETCH_USER_GAMIFICATION_REDEMPTIONS_SUCCEEDED,
  FETCH_USER_GAMIFICATION_REDEMPTIONS_FAILED,
} from "src/redux/actions/actionTypes";
import {
  AddRewardsRequest,
  ApproveGamificationRedemptionErrorResponse,
  ApproveGamificationRedemptionResponse,
  CreateRewardResponse,
  CreateRewardErrorResponse,
  DeleteRewardRequest,
  DeleteRewardResponse,
  DeleteRewardErrorResponse,
  FetchGamificationBalanceResponse,
  FetchGamificationBalanceErrorResponse,
  FetchGamificationLifetimePointsResponse,
  FetchGamificationLifetimePointsErrorResponse,
  FetchGamificationPointTriggersResponse,
  FetchGamificationPointTriggersErrorResponse,
  FetchGamificationRedemptionsResponse,
  FetchGamificationRedemptionsErrorResponse,
  FetchGamificationRewardsResponse,
  FetchGamificationRewardsErrorResponse,
  FetchGamificationTransactionsResponse,
  FetchGamificationTransactionsErrorResponse,
  GamificationBalance,
  GamificationLifetimePoints,
  GamificationPointTransaction,
  GamificationPointTrigger,
  GamificationRedemption,
  GamificationReward,
  MarkGamificationRedemptionGrantedResponse,
  MarkGamificationRedemptionGrantedErrorResponse,
  RedeemGamificationRewardRequest,
  RedeemGamificationRewardResponse,
  RedeemGamificationRewardErrorResponse,
  RejectGamificationRedemptionErrorResponse,
  RejectGamificationRedemptionResponse,
  RewardResponse,
  UpdateGamificationPointTriggerResponse,
  UpdateGamificationPointTriggerErrorResponse,
  UpdateRewardResponse,
  UpdateRewardErrorResponse,
  FetchRewardsCatalogueRequest,
  FetchRewardsCatalogueResponse,
  FetchRewardsCatalogueErrorResponse,
} from "src/types/coaching/gamification";


interface CreateRewardMutationState {
  loading: boolean;
  loaded: boolean;
  item: GamificationReward | null;
  error: any | null;
}

interface BalanceState {
  loading: boolean;
  loaded: boolean;
  item: GamificationBalance | null;
  error: any | null;
}

interface LifetimePointsState {
  loading: boolean;
  loaded: boolean;
  item: GamificationLifetimePoints | null;
  error: any | null;
}

interface TransactionsState {
  loading: boolean;
  loaded: boolean;
  items: GamificationPointTransaction[];
  error: any | null;
}

interface PointTriggerMutationState {
  loading: boolean;
  loaded: boolean;
  item: GamificationPointTrigger | null;
  error: any | null;
}

interface RedemptionMutationState {
  loading: boolean;
  loaded: boolean;
  item: GamificationRedemption | null;
  error: any | null;
}

interface RewardsState {
  loading: boolean;
  loaded: boolean;
  items: RewardResponse[];
  error: any | null;
}

interface RedeemMutationState {
  loading: boolean;
  loaded: boolean;
  error: any | null;
  lastRewardId: string | null;
}

interface GamificationState {
  redemptions: RedemptionState;
  userRedemptions: RedemptionState;
  balance: BalanceState;
  lifetimePoints: LifetimePointsState;
  rewards: RewardsState;
  redeem: RedeemMutationState;
}
interface RewardDeletionState {
  id: string;
  deleting: boolean;
  deleted: boolean;
  error: any;
}

interface PointTriggersState {
  loading: boolean;
  loaded: boolean;
  items: GamificationPointTrigger[];
  error: any | null;
}

interface GamificationState {
  redemptions: RedemptionState;
  userRedemptions: RedemptionState;
  rewards: RewardsState;
  createReward: CreateRewardMutationState;
  updateReward: CreateRewardMutationState;
  deleteReward: {
    [key: string]: RewardDeletionState;
  };
  pointTriggers: {
    loading: boolean;
    loaded: boolean;
    items: GamificationPointTrigger[];
    error: any | null;
  };
  transactions: TransactionsState;
  updatePointTrigger: PointTriggerMutationState;
  disableSettingsReward: CreateRewardMutationState;
  markRedemptionGranted: RedemptionMutationState;
  approveRedemption: RedemptionMutationState;
  rejectRedemption: RedemptionMutationState;
}

interface RedemptionState {
  loading: boolean;
  loaded: boolean;
  items: GamificationRedemption[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  error: any | null;
}

interface RewardsState {
  loading: boolean;
  loaded: boolean;
  items: RewardResponse[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  error: any | null;
}

const createRewardInitial: CreateRewardMutationState = {
  loading: false,
  loaded: false,
  item: null,
  error: null,
};

const balanceInitial: BalanceState = {
  loading: false,
  loaded: false,
  item: null,
  error: null,
};

const lifetimePointsInitial: LifetimePointsState = {
  loading: false,
  loaded: false,
  item: null,
  error: null,
};
const initialDeleteRewardState: RewardDeletionState = {
  id: "",
  deleting: false,
  deleted: false,
  error: null,
};

const pointTriggersInitial: PointTriggersState = {
  loading: false,
  loaded: false,
  items: [],
  error: null,
};

const transactionsInitial: TransactionsState = {
  loading: false,
  loaded: false,
  items: [],
  error: null,
};

const pointTriggerMutationInitial: PointTriggerMutationState = {
  loading: false,
  loaded: false,
  item: null,
  error: null,
};

const redemptionMutationInitial: RedemptionMutationState = {
  loading: false,
  loaded: false,
  item: null,
  error: null,
};

const rewardsInitial: RewardsState = {
  loading: false,
  loaded: false,
  items: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  error: null,
};

const redeemInitial: RedeemMutationState = {
  loading: false,
  loaded: false,
  error: null,
  lastRewardId: null,
};

const initialState: GamificationState = {
  redemptions: {
    loading: false,
    loaded: false,
    items: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      recordsPerPage: 0,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
    error: null,
  },
  userRedemptions: {
    loading: false,
    loaded: false,
    items: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      recordsPerPage: 0,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
    error: null,
  },
  balance: { ...balanceInitial },
  lifetimePoints: { ...lifetimePointsInitial },
  redeem: { ...redeemInitial },
  rewards: { ...rewardsInitial },
  createReward: { ...createRewardInitial },
  updateReward: { ...createRewardInitial },
  deleteReward: {},
  pointTriggers: { ...pointTriggersInitial },
  transactions: { ...transactionsInitial },
  updatePointTrigger: { ...pointTriggerMutationInitial },
  disableSettingsReward: { ...createRewardInitial },
  markRedemptionGranted: { ...redemptionMutationInitial },
  approveRedemption: { ...redemptionMutationInitial },
  rejectRedemption: { ...redemptionMutationInitial },
};

export function gamificationReducer(
  state: GamificationState = initialState,
  action: UnknownAction,
): GamificationState {
  switch (action.type) {
    
    case FETCH_GAMIFICATION_REDEMPTIONS_REQUEST:
      return {
        ...state,
        redemptions: {
          ...state.redemptions,
          loading: true,
          loaded: false,
          error: null,
        },
      };

    case FETCHING_GAMIFICATION_REDEMPTIONS_SUCCEEDED: {
      const { response } = action.payload as FetchGamificationRedemptionsResponse;

      if (!response) {
        return state;
      }

      const existingItems: GamificationRedemption[] = state.redemptions.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        redemptions: {
          ...state.redemptions,
          loading: false,
          loaded: true,
          items: records || [],
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
          error: null,
        },
      };
    }

    case FETCHING_GAMIFICATION_REDEMPTIONS_FAILED: {
      const { error } = action.payload as FetchGamificationRedemptionsErrorResponse;
      return {
        ...state,
        redemptions: {
          ...state.redemptions,
          loading: false,
          loaded: false,
          items: [],
          error: error,
        },
      };
    }

    case APPROVE_GAMIFICATION_REDEMPTION_REQUEST:
      return {
        ...state,
        approveRedemption: {
          ...state.approveRedemption,
          loading: true,
          loaded: false,
          error: null,
        },
      };

    case REJECT_GAMIFICATION_REDEMPTION_REQUEST:
      return {
        ...state,
        rejectRedemption: {
          ...state.rejectRedemption,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
      case APPROVING_GAMIFICATION_REDEMPTION_SUCCEEDED:
        case REJECTING_GAMIFICATION_REDEMPTION_SUCCEEDED: {    
          const { id, response } = action.payload as
            | ApproveGamificationRedemptionResponse
            | RejectGamificationRedemptionResponse;
        
          const prevItems = state.redemptions.items ?? [];
          const prevItem = prevItems.find((item) => item.id === id);
          const prevStatus = prevItems.find((item) => item.id === id)?.status;
          const prevRewardId = prevItem?.rewardId;   
          const nextItem = response as GamificationRedemption;
        
          const updatedItems = prevItems.map((item) =>
            item.id === id ? nextItem : item,
          );
        
          const currentTotal = state.redemptions.pagination?.totalRecordsCount ?? 0;
          const wasApprovedOrRejected =
            prevStatus === "PENDING" && nextItem.status !== "PENDING";
          const nextTotal = wasApprovedOrRejected
            ? Math.max(0, currentTotal - 1)
            : currentTotal;
        
          const nextRedemptions = {
            ...state.redemptions,
            items: updatedItems,
            pagination: {
              ...state.redemptions.pagination,
              totalRecordsCount: nextTotal,
            },
          };

          const updatedRewards = (state.rewards?.items ?? []).map((reward: RewardResponse) => {
            if (reward.id !== prevRewardId) return reward;
        
            return {
              ...reward,
              pendingRedemptionIds: (reward.pendingRedemptionIds ?? []).filter(
                (pendingId) => pendingId !== id
              ),
              ...(action.type === REJECTING_GAMIFICATION_REDEMPTION_SUCCEEDED && {
                quantity: reward.quantity + 1,
              }),
            };
          });
        
          if (action.type === APPROVING_GAMIFICATION_REDEMPTION_SUCCEEDED) {
            return {
              ...state,
              redemptions: nextRedemptions,
              rewards: {
                ...state.rewards,
                items: updatedRewards,
              },
              approveRedemption: {
                loading: false,
                loaded: true,
                item: response,
                error: null,
              },
            };
          }
        
          console.log("updatedRewards", updatedRewards);
        
          return {
            ...state,
            redemptions: nextRedemptions,
            rewards: {
              ...state.rewards,
              items: updatedRewards,
            },
            rejectRedemption: {
              loading: false,
              loaded: true,
              item: response,
              error: null,
            },
          };
        }

    case APPROVING_GAMIFICATION_REDEMPTION_FAILED: {
      const { error } = action.payload as ApproveGamificationRedemptionErrorResponse;
      return {
        ...state,
        approveRedemption: {
          ...state.approveRedemption,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case REJECTING_GAMIFICATION_REDEMPTION_FAILED: {
      const { error } = action.payload as RejectGamificationRedemptionErrorResponse;
      return {
        ...state,
        rejectRedemption: {
          ...state.rejectRedemption,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case INVALIDATE_GAMIFICATION_REDEMPTION_REVIEW:
      return {
        ...state,
        approveRedemption: { ...redemptionMutationInitial },
        rejectRedemption: { ...redemptionMutationInitial },
      };

    case FETCH_GAMIFICATION_BALANCE_REQUEST:
      return {
        ...state,
        balance: {
          ...state.balance,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
    case FETCHING_GAMIFICATION_BALANCE_SUCCEEDED: {
      const { response } = action.payload as FetchGamificationBalanceResponse;
      return {
        ...state,
        balance: {
          ...state.balance,
          loading: false,
          loaded: true,
          item: (response as GamificationBalance) || null,
          error: null,
        },
      };
    }
    
    case FETCHING_GAMIFICATION_BALANCE_FAILED: {
      const { error } = action.payload as FetchGamificationBalanceErrorResponse;
      return {
        ...state,
        balance: {
          ...state.balance,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case FETCH_GAMIFICATION_LIFETIME_POINTS_REQUEST:
      return {
        ...state,
        lifetimePoints: {
          ...state.lifetimePoints,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
    case FETCHING_GAMIFICATION_LIFETIME_POINTS_SUCCEEDED: {
      const { response } =
        action.payload as FetchGamificationLifetimePointsResponse;
      return {
        ...state,
        lifetimePoints: {
          ...state.lifetimePoints,
          loading: false,
          loaded: true,
          item: (response as GamificationLifetimePoints) || null,
          error: null,
        },
      };
    }

    case FETCHING_GAMIFICATION_LIFETIME_POINTS_FAILED: {
      const { error } =
        action.payload as FetchGamificationLifetimePointsErrorResponse;
      return {
        ...state,
        lifetimePoints: {
          ...state.lifetimePoints,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case FETCH_GAMIFICATION_TRANSACTIONS_REQUEST:
      return {
        ...state,
        transactions: {
          ...state.transactions,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
    case FETCHING_GAMIFICATION_TRANSACTIONS_SUCCEEDED: {
      const { response } = action.payload as FetchGamificationTransactionsResponse;
      return {
        ...state,
        transactions: {
          ...state.transactions,
          loading: false,
          loaded: true,
          items: response ?? [],
          error: null,
        },
      };
    }

    case FETCHING_GAMIFICATION_TRANSACTIONS_FAILED: {
      const { error } = action.payload as FetchGamificationTransactionsErrorResponse;
      return {
        ...state,
        transactions: {
          ...state.transactions,
          loading: false,
          loaded: false,
          items: [],
          error,
        },
      };
    }

    case FETCH_REWARDS_CATALOGUE_REQUEST:
      return {
        ...state,
        rewards: { ...state.rewards, loading: true, loaded: false, error: null, items: [] },
      };

    case FETCHING_REWARDS_CATALOGUE_SUCCEEDED: {
      const { response } = action.payload as FetchRewardsCatalogueResponse;
    
      const existingItems: GamificationReward[] = state.rewards.items;
    
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }
    
      records = _.uniqBy(records, "id");
    
      return {
        ...state,
        rewards: {
          ...state.rewards,
          loading: false,
          loaded: true,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }
    case FETCHING_REWARDS_CATALOGUE_FAILED: {
      const { error } = action.payload as FetchRewardsCatalogueErrorResponse;
      return {
        ...state,
        rewards: { ...state.rewards, loading: false, loaded: false, items: [], error },
      };
    }

    case REDEEM_GAMIFICATION_REWARD_REQUEST: {
      const { rewardId } = action.payload as RedeemGamificationRewardRequest;
      return {
        ...state,
        redeem: {
          ...state.redeem,
          loading: true,
          loaded: false,
          error: null,
          lastRewardId: rewardId,
        },
      };
    }

    case REDEEMING_GAMIFICATION_REWARD_SUCCEEDED: {
      const { rewardId, response } = action.payload as RedeemGamificationRewardResponse;
      const redemption = response as GamificationRedemption;
      const prevItems = state.redemptions.items || [];
      const prevUserItems = state.userRedemptions.items || [];

      const catalogItems: GamificationReward[] = state.rewards.items || [];
      const nextCatalogItems = catalogItems.map((reward) => {
        if (reward.id !== rewardId) return reward;
        if (reward.quantity == null || reward.quantity < 0) return reward;
        return { ...reward, quantity: Math.max(0, reward.quantity - 1) };
      });

      return {
        ...state,
        redeem: {
          ...state.redeem,
          loading: false,
          loaded: true,
          error: null,
          lastRewardId: null,
        },
        rewards: {
          ...state.rewards,
          items: nextCatalogItems,
        },
        redemptions: {
          ...state.redemptions,
          items: [redemption, ...prevItems.filter((r) => r.id !== redemption.id)],
        },
        userRedemptions: {
          ...state.userRedemptions,
          items: [redemption, ...prevUserItems.filter((r) => r.id !== redemption.id)],
        },
      };
    }

    case REDEEMING_GAMIFICATION_REWARD_FAILED: {
      const { error } = action.payload as RedeemGamificationRewardErrorResponse;
      return {
        ...state,
        redeem: {
          ...state.redeem,
          loading: false,
          loaded: false,
          error,
          lastRewardId: null,
        },
      };
    }

    case INVALIDATE_GAMIFICATION_REWARD_REDEMPTION:
      return {
        ...state,
        redeem: { ...redeemInitial },
      };

    case MARK_GAMIFICATION_REDEMPTION_GRANTED_REQUEST:
      return {
        ...state,
        markRedemptionGranted: {
          ...state.markRedemptionGranted,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
    case MARKING_GAMIFICATION_REDEMPTION_GRANTED_SUCCEEDED: {
      const { id, response } = action.payload as MarkGamificationRedemptionGrantedResponse;
      const nextItem = response as GamificationRedemption;
      const prevItems = state.redemptions.items ?? [];
      return {
        ...state,
        markRedemptionGranted: {
          ...state.markRedemptionGranted,
          loading: false,
          loaded: true,
          item: nextItem,
          error: null,
        },
        redemptions: {
          ...state.redemptions,
          items: prevItems.map((item) => (item.id === id ? nextItem : item)),
        },
      };
    }

    case MARKING_GAMIFICATION_REDEMPTION_GRANTED_FAILED: {
      const { error } = action.payload as MarkGamificationRedemptionGrantedErrorResponse;
      return {
        ...state,
        markRedemptionGranted: {
          ...state.markRedemptionGranted,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case FETCH_GAMIFICATION_POINT_TRIGGERS_REQUEST:
      return {
        ...state,
        pointTriggers: {
          ...state.pointTriggers,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
    case FETCHING_GAMIFICATION_POINT_TRIGGERS_SUCCEEDED: {
      const { response } = action.payload as FetchGamificationPointTriggersResponse;
      return {
        ...state,
        pointTriggers: { loading: false, loaded: true, items: response || [], error: null },
      };
    }

    case FETCHING_GAMIFICATION_POINT_TRIGGERS_FAILED: {
      const { error } = action.payload as FetchGamificationPointTriggersErrorResponse;
      return {
        ...state,
        pointTriggers: { loading: false, loaded: false, items: [], error },
      };
    }

    case UPDATE_GAMIFICATION_POINT_TRIGGER_REQUEST:
      return {
        ...state,
        updatePointTrigger: {
          ...state.updatePointTrigger,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    
    case UPDATING_GAMIFICATION_POINT_TRIGGER_SUCCEEDED: {
      const { id, response } = action.payload as UpdateGamificationPointTriggerResponse;
      return {
        ...state,
        updatePointTrigger: {
          ...state.updatePointTrigger,
          loading: false,
          loaded: true,
          item: response,
          error: null,
        },
        pointTriggers: {
          ...state.pointTriggers,
          items: state.pointTriggers.items.map((row) =>
            row.id === id ? response : row,
          ),
        },
      };
    }

    case UPDATING_GAMIFICATION_POINT_TRIGGER_FAILED: {
      const { error } = action.payload as UpdateGamificationPointTriggerErrorResponse;
      return {
        ...state,
        updatePointTrigger: {
          ...state.updatePointTrigger,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case CREATE_REWARD_REQUEST: {
      return {
        ...state,
        createReward: {
          ...state.createReward,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    }

    case CREATING_REWARD_SUCCEEDED: {
      const { response } = action.payload as CreateRewardResponse;
      return {
        ...state,
        createReward: {
          loading: false,
          loaded: true,
          item: response,
          error: null,
        },
      };
    }

    case CREATING_REWARD_FAILED: {
      const { error } = action.payload as CreateRewardErrorResponse;
      return {
        ...state,
        createReward: {
          ...state.createReward,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case INVALIDATE_CREATE_REWARD_STATE:
      return {
        ...state,
        createReward: { ...createRewardInitial },
      };

    case UPDATE_REWARD_REQUEST: {
      return {
        ...state,
        updateReward: {
          ...state.updateReward,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    }

    case UPDATING_REWARD_SUCCEEDED: {
      const { id, response } = action.payload as UpdateRewardResponse;
      return {
        ...state,
        updateReward: {
          loading: false,
          loaded: true,
          item: response,
          error: null,
        },
        rewards: {
          ...state.rewards,
          items: state.rewards.items.map((row) => {
            if (row.id === id) {
              return response as RewardResponse;
            }
            return row;
          }),
        },
      };
    }

    case UPDATING_REWARD_FAILED: {
      const { error } = action.payload as UpdateRewardErrorResponse;
      return {
        ...state,
        updateReward: {
          ...state.updateReward,
          loading: false,
          loaded: false,
          item: null,
          error,
        },
      };
    }

    case ADD_REWARDS_TO_CATALOGUE_REQUEST: {
      const { records, addAt } = action.payload as AddRewardsRequest;
      let existingItems = state.rewards.items || [];

      if (addAt === "start") {
        existingItems = [...records, ...existingItems];
      } else {
        existingItems = [...existingItems, ...records];
      }

      existingItems = _.uniqBy(existingItems, "id");

      return {
        ...state,
        rewards: {
          ...state.rewards,
          items: existingItems,
        },
      };
    }

    case FETCH_GAMIFICATION_REWARDS_REQUEST: {
      return {
        ...state,
        rewards: {
          ...state.rewards,
          loading: true,
        },
      };
    }

    case FETCHING_GAMIFICATION_REWARDS_SUCCEEDED: {
      const { response } = action.payload as FetchGamificationRewardsResponse;

      const existingItems: RewardResponse[] = state.rewards.items;

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");

      return {
        ...state,
        rewards: {
          ...state.rewards,
          loading: false,
          loaded: true,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case FETCHING_GAMIFICATION_REWARDS_FAILED: {
      const { error } = action.payload as FetchGamificationRewardsErrorResponse;
      return {
        ...state,
        rewards: {
          ...state.rewards,
          loading: false,
          error,
        },
      };
    }

    case DELETE_REWARD_REQUEST: {
      const { id } = action.payload as DeleteRewardRequest;
      const deletionState = state.deleteReward[id] || initialDeleteRewardState;
      return {
        ...state,
        deleteReward: {
          ...state.deleteReward,
          [id]: {
            ...deletionState,
            id,
            deleting: true,
            deleted: false,
            error: null,
          },
        },
      };
    }

    case DELETING_REWARD_SUCCEEDED: {
      const { id } = action.payload as DeleteRewardResponse;
      const deletionState = state.deleteReward[id];
      if (!deletionState) return state;
      return {
        ...state,
        deleteReward: {
          ...state.deleteReward,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: true,
            error: null,
          },
        },
      };
    }

    case DELETING_REWARD_FAILED: {
      const { id, error } = action.payload as DeleteRewardErrorResponse;
      const deletionState = state.deleteReward[id];
      if (!deletionState) return state;
      return {
        ...state,
        deleteReward: {
          ...state.deleteReward,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: false,
            error,
          },
        },
      };
    }

    case INVALIDATE_DELETE_REWARD_STATE: {
      const id = action.payload as string;
      return {
        ...state,
        deleteReward: _.omit(state.deleteReward, id),
      };
    }

    case FETCH_USER_GAMIFICATION_REDEMPTIONS_REQUEST:
      return {
        ...state,
        userRedemptions: {
          ...state.userRedemptions,
          loading: true,
          loaded: false,
          error: null,
        },
      };

    case FETCH_USER_GAMIFICATION_REDEMPTIONS_SUCCEEDED: {
      const { response } = action.payload as FetchGamificationRedemptionsResponse;

      if (!response) {
        return state;
      }

      const existingItems: GamificationRedemption[] = state.userRedemptions.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        userRedemptions: {
          ...state.userRedemptions,
          loading: false,
          loaded: true,
          items: records || [],
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
          error: null,
        },
      };
    }

    case FETCH_USER_GAMIFICATION_REDEMPTIONS_FAILED: {
      const { error } = action.payload as FetchGamificationRedemptionsErrorResponse;
      return {
        ...state,
        userRedemptions: {
          ...state.userRedemptions,
          loading: false,
          loaded: false,
          items: [],
          error: error,
        },
      };
    }

    default:
      return state;
  }
}
